/**
 * Offer builder focus mode (Asana 1219788320590626, Decisions 1, 9, 10, 11).
 *
 * Pure reducer so the threshold, the per-side opt-out and the nav bookkeeping
 * are unit-testable without React. The page owns the instance (never the URL,
 * never the builder's persisted sessionStorage store).
 */

export type FocusSide = 'send' | 'receive';

/** A full list strictly larger than this auto-focuses when chosen (Decision 9). */
export const AUTO_FOCUS_THRESHOLD = 20;

export interface OfferFocusState {
  /** null = four columns as today. */
  focusSide: FocusSide | null;
  /** Decision 10: Show both sides while on that side's full list opts it out until reload. */
  autoFocusDeclined: Record<FocusSide, boolean>;
  /** Decision 11: the builder folded the nav on auto-focus and may re-open it on return. */
  navAutoCollapsed: boolean;
}

export const INITIAL_OFFER_FOCUS_STATE: OfferFocusState = {
  focusSide: null,
  autoFocusDeclined: { send: false, receive: false },
  navAutoCollapsed: false,
};

export type OfferFocusAction =
  /** Expand button, band chevron, nudge click: manual, never folds the nav. */
  | { type: 'focus'; side: FocusSide }
  /** Decision 9 only. navWasOpen records whether the page folded the nav. */
  | { type: 'auto_focus'; side: FocusSide; navWasOpen: boolean }
  /** Show both sides, the strip, the phone back chevron. */
  | { type: 'unfocus'; sideOnFullList: boolean }
  /** The user toggled the nav while the builder held it folded: their choice wins. */
  | { type: 'nav_toggled_manually' }
  /** The page released its collapse request on return to four columns. */
  | { type: 'nav_restored' };

export const otherSide = (side: FocusSide): FocusSide => (side === 'send' ? 'receive' : 'send');

export const offerFocusReducer = (state: OfferFocusState, action: OfferFocusAction): OfferFocusState => {
  switch (action.type) {
    case 'focus':
      return state.focusSide === action.side ? state : { ...state, focusSide: action.side };
    case 'auto_focus':
      return { ...state, focusSide: action.side, navAutoCollapsed: action.navWasOpen };
    case 'unfocus': {
      if (state.focusSide === null) return state;
      const declined = action.sideOnFullList
        ? { ...state.autoFocusDeclined, [state.focusSide]: true }
        : state.autoFocusDeclined;
      // navAutoCollapsed is left for the page to read (re-open the nav or not),
      // then cleared by nav_restored.
      return { ...state, focusSide: null, autoFocusDeclined: declined };
    }
    case 'nav_toggled_manually':
    case 'nav_restored':
      return state.navAutoCollapsed ? { ...state, navAutoCollapsed: false } : state;
    default:
      return state;
  }
};

/**
 * Decision 9 gate. An unknown total (the count probe has not answered yet)
 * never auto-focuses: the list must be known to be large, not guessed.
 */
export const shouldAutoFocus = (
  state: OfferFocusState,
  side: FocusSide,
  fullListTotal: number | undefined,
): boolean =>
  fullListTotal != null &&
  fullListTotal > AUTO_FOCUS_THRESHOLD &&
  !state.autoFocusDeclined[side] &&
  state.focusSide !== side;
